import React, { useState, useEffect } from 'react';
import { Shield, FileText, RefreshCw, Download, Copy, CheckCircle, AlertCircle, Upload } from 'lucide-react';
import { Button } from '../ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DisputeNarrativeTab = ({ claimId, userId, claim }) => {
  const [narrative, setNarrative] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [policyStatus, setPolicyStatus] = useState(null);
  const [isUploadingPolicy, setIsUploadingPolicy] = useState(false);
  const [adjusterDocs, setAdjusterDocs] = useState([]);
  const [isUploadingAdjuster, setIsUploadingAdjuster] = useState(false);

  useEffect(() => {
    loadNarrative();
    checkPolicyStatus();
    loadAdjusterDocs();
  }, [claimId]);

  const loadNarrative = async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/claims/${claimId}/dispute-narrative`, {
        params: { user_id: userId }
      });
      if (res.data.success) {
        setNarrative(res.data);
      }
    } catch (err) {
      console.error('Error loading narrative:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const checkPolicyStatus = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/claims/${claimId}/policy-analysis`, {
        params: { user_id: userId }
      });
      setPolicyStatus(res.data.success ? 'analyzed' : 'not_analyzed');
    } catch {
      setPolicyStatus('none');
    }
  };

  const loadAdjusterDocs = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/claims/${claimId}/documents`, {
        params: { user_id: userId }
      });
      const docs = (res.data || []).filter(d => d.category === 'adjuster_report');
      setAdjusterDocs(docs);
    } catch { /* no docs yet */ }
  };

  const handleAdjusterUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    
    setIsUploadingAdjuster(true);
    let uploadCount = 0;
    
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('description', 'Insurance adjuster documentation');
        formData.append('is_evidence', 'true');
        
        await axios.post(
          `${BACKEND_URL}/api/claims/${claimId}/upload-photo?user_id=${userId}&category=adjuster_report`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        uploadCount++;
      } catch (err) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    
    if (uploadCount > 0) {
      toast.success(`Uploaded ${uploadCount} adjuster document(s)`);
      loadAdjusterDocs();
    }
    setIsUploadingAdjuster(false);
    e.target.value = '';
  };

  const handleGenerate = async () => {
    setIsGenerating(true);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/claims/${claimId}/generate-dispute-narrative`,
        null,
        { params: { user_id: userId } }
      );
      if (res.data.success) {
        setNarrative(res.data);
        toast.success('Dispute narrative generated');
      }
    } catch (err) {
      console.error('Error generating narrative:', err);
      toast.error(err.response?.data?.detail || 'Failed to generate narrative');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCopy = () => {
    if (narrative?.narrative) {
      navigator.clipboard.writeText(narrative.narrative);
      setCopied(true);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleDownload = () => {
    if (!narrative?.narrative) return;
    const blob = new Blob([narrative.narrative], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `dispute-narrative-${claimId}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handlePolicyUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setIsUploadingPolicy(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/claims/${claimId}/upload-policy?user_id=${userId}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      if (res.data.success) {
        toast.success(`Policy uploaded (${res.data.text_length} chars extracted)`);
        setPolicyStatus('uploaded');
        // Auto-analyze
        try {
          await axios.post(`${BACKEND_URL}/api/claims/${claimId}/analyze-policy`, null, {
            params: { user_id: userId }
          });
          setPolicyStatus('analyzed');
          toast.success('Policy analyzed');
        } catch {
          toast.info('Policy uploaded. Click "Analyze Policy" on the Policy Analysis tab to process it.');
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setIsUploadingPolicy(false);
      e.target.value = '';
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow p-8 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mx-auto mb-3"></div>
        <p className="text-gray-500">Loading dispute data...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dispute-narrative-tab">
      {/* Status Banner */}
      <div className={`rounded-lg p-4 ${
        policyStatus === 'analyzed' ? 'bg-green-50 border border-green-200' :
        policyStatus === 'uploaded' ? 'bg-blue-50 border border-blue-200' :
        'bg-amber-50 border border-amber-200'
      }`}>
        <div className="flex items-start gap-3">
          {policyStatus === 'analyzed' ? (
            <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
          ) : (
            <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
          )}
          <div className="flex-1">
            <p className="font-medium text-gray-900">
              {policyStatus === 'analyzed' ? 'Policy Analyzed - Ready for Dispute' :
               policyStatus === 'uploaded' ? 'Policy Uploaded - Analysis Pending' :
               'Upload Insurance Policy for Best Results'}
            </p>
            <p className="text-sm text-gray-600 mt-1">
              {policyStatus === 'analyzed' 
                ? 'The AI will reference specific policy language, coverage details, and exclusions in the dispute narrative.' 
                : 'Upload the homeowner\'s insurance policy (PDF or image) to enable AI-powered policy analysis and citation in the dispute narrative.'}
            </p>
            {policyStatus !== 'analyzed' && (
              <div className="mt-3">
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx"
                    multiple
                    onChange={handlePolicyUpload}
                    className="hidden"
                    data-testid="policy-upload-input"
                  />
                  <span className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 cursor-pointer">
                    <Upload className="w-4 h-4" />
                    {isUploadingPolicy ? 'Uploading...' : 'Upload Policy (PDF or Image)'}
                  </span>
                </label>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Adjuster Report / Insurance Company Documentation */}
      <div className="bg-white rounded-lg shadow p-6" data-testid="adjuster-docs-section">
        <h3 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
          <FileText className="w-5 h-5 text-blue-600" />
          Insurance Adjuster Documentation
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          Upload the insurance company's adjuster report, denial letter, scope of loss, or any correspondence to use in your dispute.
        </p>
        
        {/* Show existing adjuster docs */}
        {adjusterDocs.length > 0 && (
          <div className="mb-4 space-y-2">
            {adjusterDocs.map(doc => (
              <div key={doc.id} className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <FileText className="w-5 h-5 text-blue-600 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">{doc.file_name || doc.filename}</p>
                  <p className="text-xs text-gray-500">{doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString() : ''}</p>
                </div>
                {doc.supabase_url && (
                  <a href={doc.supabase_url} target="_blank" rel="noreferrer" className="text-blue-600 hover:text-blue-800">
                    <Download className="w-4 h-4" />
                  </a>
                )}
              </div>
            ))}
          </div>
        )}
        
        <label className="cursor-pointer">
          <input
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.xls,.xlsx,video/*"
            multiple
            onChange={handleAdjusterUpload}
            className="hidden"
            data-testid="adjuster-doc-upload"
          />
          <span className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 cursor-pointer">
            <Upload className="w-4 h-4" />
            {isUploadingAdjuster ? 'Uploading...' : 'Upload Adjuster Report / Documentation'}
          </span>
        </label>
      </div>

      {/* Evidence Summary */}
      {narrative?.evidence_used && (
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="font-semibold text-gray-800 mb-2">Evidence Used</h3>
          <div className="flex gap-4 text-sm">
            <span className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full">
              {narrative.evidence_used.photos || 0} Photos
            </span>
            <span className="px-3 py-1 bg-green-50 text-green-700 rounded-full">
              {narrative.evidence_used.field_reports || 0} Field Reports
            </span>
            <span className="px-3 py-1 bg-purple-50 text-purple-700 rounded-full">
              {narrative.evidence_used.lead_files || 0} Lead Files
            </span>
            {narrative.has_policy_analysis && (
              <span className="px-3 py-1 bg-orange-50 text-orange-700 rounded-full">
                Policy Analysis Included
              </span>
            )}
          </div>
        </div>
      )}

      {/* Generate / Regenerate Button */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center">
            <Shield className="w-5 h-5 mr-2 text-red-600" />
            Denial Dispute Narrative
          </h2>
          <div className="flex gap-2">
            {narrative?.narrative && (
              <>
                <Button variant="outline" size="sm" onClick={handleCopy} data-testid="copy-narrative">
                  {copied ? <CheckCircle className="w-4 h-4 mr-1 text-green-600" /> : <Copy className="w-4 h-4 mr-1" />}
                  {copied ? 'Copied' : 'Copy'}
                </Button>
                <Button variant="outline" size="sm" onClick={handleDownload} data-testid="download-narrative">
                  <Download className="w-4 h-4 mr-1" />
                  Download
                </Button>
              </>
            )}
            <Button
              onClick={handleGenerate}
              disabled={isGenerating}
              className="bg-red-600 hover:bg-red-700"
              data-testid="generate-narrative-btn"
            >
              {isGenerating ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : narrative?.narrative ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Regenerate
                </>
              ) : (
                <>
                  <FileText className="w-4 h-4 mr-2" />
                  Generate Dispute Narrative
                </>
              )}
            </Button>
          </div>
        </div>

        {narrative?.generated_at && (
          <p className="text-xs text-gray-500 mb-3">
            Generated: {new Date(narrative.generated_at).toLocaleString()}
          </p>
        )}

        {narrative?.narrative ? (
          <div className="bg-gray-50 rounded-lg p-6 whitespace-pre-wrap text-sm leading-relaxed font-mono border" data-testid="narrative-content">
            {narrative.narrative}
          </div>
        ) : (
          <div className="bg-gray-50 rounded-lg p-8 text-center text-gray-500">
            <Shield className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p className="font-medium">No dispute narrative generated yet</p>
            <p className="text-sm mt-1">
              Click "Generate Dispute Narrative" to create an AI-powered dispute letter using all collected evidence, 
              policy analysis, damage documentation, and estimate data.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DisputeNarrativeTab;
